import { gql } from '@apollo/client';
import _ from 'lodash';
import { captureException } from '@playbookux/playbook-web-core';

export const TEMPLATE_FRAGMENT = gql`
  fragment TemplateFragment on Template {
    id
    name
    description
    device,
    operatingSystem
    templateContext
    testerRequirements
    language
    company {
      id
      name
    }
  }
`;

export const AUDIENCE_ATTRIBUTES_FRAGMENT = gql`
  fragment AudienceAttributesFragment on Template {
    audienceAttributes {
      id
      audienceAttributeType
      audienceAttributeOptions {
        id
        value
        name
      }
    }
  }
`;

export const SURVEY_FRAGMENT = gql`
  fragment SurveyFragment on Survey {
    id
    surveyType
  }
`;

export const QUESTION_FRAGMENT = gql`
  fragment QuestionFragment on Question {
    id
    label
    questionType
    questionOrder
    groupId
    leftLabel
    rightLabel
    blurVideo
    imageKey
    imageUrl
    videoKey
    videoUrl
    randomize
    questionOptions {
      id
      label
      disqualify
      qualify
      optionType
      order
    }
  }
`;

export const AUDIENCE_ATTRIBUTE_FRAGMENT = gql`
  fragment AudienceAttributeFragment on AudienceAttribute {
    id
    audienceAttributeType
    audienceAttributeOptions {
      id
      value
    }
  }
`;

export const GET_TEMPLATE_WITH_SURVEY_BY_TEMPLATE_ID = gql`
    query Template($templateId: ID!) {
        template(templateId: $templateId) {
          ...TemplateFragment
          ...AudienceAttributesFragment
          survey {
            ...SurveyFragment
            questions {
              ...QuestionFragment
            }
          }
        }
      }

      ${AUDIENCE_ATTRIBUTES_FRAGMENT}
      ${TEMPLATE_FRAGMENT}
      ${SURVEY_FRAGMENT}
      ${QUESTION_FRAGMENT}
`;

export const CREATE_TEMPLATE = gql`
      mutation CreateTemplate(
        $projectId: ID,
        $segmentId: ID,
        $attributes: TemplateAttributes!
      ) {
      createTemplate(
        projectId: $projectId,
        segmentId: $segmentId,
        attributes: $attributes
      ) {
        template {
          ...TemplateFragment
        }
      }
    }
    ${TEMPLATE_FRAGMENT}
`;

export const UPDATE_TEMPLATE = gql`
      mutation UpdateTemplate(
        $id: ID!
        $attributes: TemplateAttributes!
      ) {
      updateTemplate(
        id: $id
        attributes: $attributes
      ) {
        template {
          ...TemplateFragment
        }
      }
    }
    ${TEMPLATE_FRAGMENT}
`;

export const DELETE_TEMPLATE = gql`
    mutation DeleteTemplate($id: ID!) {
      deleteTemplate(id: $id) {
        void
    }
  }
`;

export const GET_PAGINATED_TEMPLATES = gql`
  query Templates(
    $companyId: ID
    $limit: Int,
    $offset: Int,
    $templateType: TemplateTypeEnum
  ) {
    templates(
      companyId: $companyId
      limit: $limit
      offset: $offset
      templateType: $templateType
    ) {
        ...TemplateFragment
        survey {
          ...SurveyFragment
          questions {
            ...QuestionFragment
          }
        }
        audienceAttributes {
        ...AudienceAttributeFragment
        }
      }
    }
    ${TEMPLATE_FRAGMENT}
    ${SURVEY_FRAGMENT}
    ${QUESTION_FRAGMENT}
    ${AUDIENCE_ATTRIBUTE_FRAGMENT}
`;

export const updateTemplateDeleted = (templateId, templateType, companyId) => (cache) => {
  const variables = { companyId, templateType };

  const { templates: cachedTemplate } = cache.readQuery({
    query: GET_PAGINATED_TEMPLATES,
    variables,
    // FIXME: The moment we refactor the templates, make sure the template test and audience does not call using templateType = consent
    skip: !templateType || templateType === 'consent',
  });

  const templa = _.find(cachedTemplate, (template) => template.id === templateId);

  cache.evict({ id: cache.identify(templa) });
  cache.gc();
};

export const updateCacheOnCreateTemplate = (templateType, companyId) => (cache, { data }) => {
  try {
    const { createTemplate: { template } } = data;

    const variables = { companyId, templateType };

    const { templates: cachedTemplates } = cache.readQuery({
      query: GET_PAGINATED_TEMPLATES,
      variables,
      // FIXME: The moment we refactor the templates, make sure the template test and audience does not call using templateType = consent
      skip: !templateType || templateType === 'consent',
    });
    const templates = [...cachedTemplates, template];
    cache.writeQuery({
      query: GET_PAGINATED_TEMPLATES,
      variables,
      data: { templates },
      // FIXME: The moment we refactor the templates, make sure the template test and audience does not call using templateType = consent
      skip: !templateType || templateType === 'consent',
    });
  } catch (e) {
    captureException('INTEGRATION_TEST', e);
  }
};
